import { observer } from 'mobx-react-lite'
import type { DocumentRequestedSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/documents/DocumentRequestedSupportingItemViewModel'
import {
  RequestedDocumentCard,
  RequestedDocumentFileListItem,
  RequestedDocumentFileListSkeletonItem,
  type RequestedDocumentCardProps,
} from './'
import { SkeletonList } from '@/components/Skeleton'
import { LinkDocumentsDialog } from '@/features/docs/components/LinkDocumentsDialog'
import type { DocumentViewModel } from '@/features/docs/DocumentViewModel'
import { match } from '@/utils/pattern-match-util'

interface RequestedDocumentCardContainerProps {
  vm: DocumentRequestedSupportingItemViewModel
  variant?: RequestedDocumentCardProps['variant']
  isDisabled?: boolean
}

/**
 * Requested Document Card Container
 */
export const RequestedDocumentCardContainer = observer(
  function RequestedDocumentCardContainer({
    vm,
    variant,
    isDisabled,
  }: RequestedDocumentCardContainerProps) {
    isDisabled ||= vm.fetch.pending
    return (
      <>
        <RequestedDocumentCard
          title={vm.item.name}
          description={vm.item.shortDescription}
          onUploadNew={vm.upload}
          isDisabled={isDisabled}
          onLinkExisting={vm.linkDocumentsVm.show}
          variant={variant}
          testId={`requested-document-card-${vm.item.input.documentTypeShortId}`}
          triggerTestId={`requested-document-card-actions-${vm.item.input.documentTypeShortId}`}
          uploadNewTestId={`requested-document-card-upload-new-${vm.item.input.documentTypeShortId}`}
          linkExistingTestId={`requested-document-card-link-existing-${vm.item.input.documentTypeShortId}`}
        >
          {vm.fetch.pending ? (
            <SkeletonList count={vm.documentCount}>
              <RequestedDocumentFileListSkeletonItem />
            </SkeletonList>
          ) : (
            <>
              {vm.documentsListVm.allDocuments.map((docVm) => (
                <RequestedDocumentFileListItemContainer
                  key={docVm.doc.id}
                  vm={vm}
                  docVm={docVm}
                  isDisabled={isDisabled}
                />
              ))}
              {vm.documentsListVm.uploaderVm.pendingDocuments.map(
                (pendingVm) => (
                  <RequestedDocumentFileListItem
                    key={pendingVm.cid}
                    isDisabled={isDisabled}
                    fileName={pendingVm.fileName}
                    progress={pendingVm.progressPercent}
                  />
                ),
              )}
            </>
          )}
        </RequestedDocumentCard>
        <LinkDocumentsDialog
          vm={vm.linkDocumentsVm}
          testId="link-documents-dialog"
          doneButtonTestId="link-documents-dialog-done-button"
        />
      </>
    )
  },
)

interface RequestedDocumentFileListItemContainerProps {
  vm: DocumentRequestedSupportingItemViewModel
  docVm: DocumentViewModel
  isDisabled: boolean
}

/**
 * Requested Document File List Item Container
 */
export const RequestedDocumentFileListItemContainer = observer(
  function RequestedDocumentFileListItemContainer({
    vm,
    docVm,
    isDisabled,
  }: RequestedDocumentFileListItemContainerProps) {
    const access = docVm.doc.accesses.find((a) =>
      match(a.accessType, 'type', {
        Job: (jobAccess) => jobAccess.jobId === vm.job.id,
        Unknown: () => false,
      }),
    )
    return (
      <RequestedDocumentFileListItem
        fileName={docVm.doc.title}
        onRemovePress={() => docVm.remove(access)}
        isDisabled={isDisabled}
        removeButtonTestId={`requested-document-file-list-item-remove-button-${docVm.doc.title}`}
      />
    )
  },
)
